<script setup lang="ts">
import {
  mdiDiamondStone,
  mdiGift,
  mdiHeart,
  mdiHeartOutline,
  mdiTrashCan,
} from '@quasar/extras/mdi-v7';
import TimerChip from 'src/shared/components/TimerChip/TimerChip.vue';
import ScoreChip from 'src/shared/components/ScoreChip/ScoreChip.vue';
import { formatTime } from 'shared/composables/useTimer/useTimer';
import { ref } from 'vue';
import BButton from 'src/shared/components/base/BButton/BButton.vue';
import { normalizePersistedAccumulatorMs } from 'src/shared/utilities/timeAccumulator';

defineProps<{
  image?: string;
  name?: string;
  subtitle?: string;
  description?: string;
  isFavorite?: boolean;
  hasFavorite?: boolean;
  actionButtonIcon?: string;
  timeAccumulator?: number;
  score?: number;
  isRemove?: boolean;
  isFree?: boolean | null;
}>();

const emit = defineEmits<{
  'toggle:favorite': [];
  'action:click': [];
  'action:remove': [];
}>();

const expandDescription = ref(false);
</script>

<template>
  <q-card>
    <q-card-section
      tag="header"
      class="relative q-pa-none full-width"
    >
      <q-img
        :src="image as string"
        :alt="name"
        ratio="16/9"
        style="min-height: 80px; object-fit: cover"
        class="img-placeholder"
      >
        <template #error>
          <div class="absolute-full flex flex-center img-fallback">
            <q-icon
              name="image_off"
              size="48px"
            />
          </div>
        </template>
      </q-img>

      <div class="absolute-top-right">
        <slot name="card-header-right" />
      </div>
    </q-card-section>
    <q-card-section class="q-pa-none">
      <slot name="progress" />
    </q-card-section>
    <q-card-section
      tag="section"
      class="q-pt-sm q-pb-xs relative-position full-width"
      :class="{
        'fixed-desc-height': description && !expandDescription,
      }"
    >
      <slot name="title">
        <q-item
          dense
          class="q-pa-none"
        >
          <q-item-section>
            <h3
              class="text-h6 ellipsis full-width"
              :title="name"
            >
              {{ name }}
            </h3>
          </q-item-section>
          <q-item-section side>
            <q-item-label class="q-gutter-x-xs row items-center no-wrap">
              <q-icon
                v-if="isFree !== undefined && isFree !== null"
                :name="isFree ? mdiGift : mdiDiamondStone"
                :color="isFree ? 'positive' : 'warning'"
                size="18px"
                :title="
                  isFree ? $t('COURSE_TYPE_FREE') : $t('COURSE_TYPE_PREMIUM')
                "
              />
              <q-btn
                v-if="hasFavorite"
                :icon="isFavorite ? mdiHeart : mdiHeartOutline"
                color="secondary"
                dense
                :title="
                  isFavorite
                    ? $t('REMOVE_FROM_FAVORITES')
                    : $t('ADD_TO_FAVORITES')
                "
                size="sm"
                text-color="dark"
                @click.stop="emit('toggle:favorite')"
              />
              <q-btn
                v-if="isRemove"
                :icon="mdiTrashCan"
                color="secondary"
                dense
                :title="$t('REMOVE_COURSE')"
                size="sm"
                text-color="dark"
                @click.stop="emit('action:remove')"
              />
            </q-item-label>
          </q-item-section>
        </q-item>
      </slot>
      <slot name="subtitle">
        <h4 class="text-subtitle2 text-primary ellipsis">
          {{ subtitle }}
        </h4>
      </slot>
      <!-- Truncated description only if panel is not expanded -->
      <slot name="description">
        <p
          v-if="description"
          class="q-mt-none text-caption text-border q-py-xs"
          :class="{
            'ellipsis-4-lines': !expandDescription,
          }"
          :title="description"
          @click="expandDescription = !expandDescription"
        >
          {{ description }}
        </p>
      </slot>
      <!-- FAB 50% in body -->
    </q-card-section>
    <q-space />
    <q-card-section class="q-py-none .fixed-chips-height">
      <TimerChip
        v-if="timeAccumulator"
        color="dark"
        dense
        dark
        :label="
          formatTime(normalizePersistedAccumulatorMs(timeAccumulator), 'min')
        "
      />
      <ScoreChip
        v-if="score"
        color="positive"
        dense
      >
        {{ score }}
      </ScoreChip>
    </q-card-section>
    <q-card-section
      tag="footer"
      class="column justify-center q-py-sm fixed-footer-height relative-position"
    >
      <slot name="action-button">
        <BButton
          v-if="actionButtonIcon"
          :label="$t('GO_TO_MY_COURSE')"
          text-color="dark"
          class="text-weight-bolder"
          color="secondary"
          size="custom"
          @click.stop="emit('action:click')"
        />
      </slot>
    </q-card-section>
  </q-card>
</template>

<style scoped>
.my-card {
  transition:
    box-shadow 0.2s,
    transform 0.2s;
}
.my-card:hover {
  box-shadow: 0 8px 32px 0 rgba(60, 60, 60, 0.18);
  transform: translateY(-2px) scale(1.02);
}
.ellipsis-4-lines {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
}
.fixed-desc-height {
  height: 8.75em;
}
.fixed-chips-height {
  height: 2em;
}
.fixed-footer-height {
  height: 60px;
}

/* Cover-image tint and the broken-image fallback. These were fixed `bg-grey-2`
   / `bg-grey-3 text-grey-7`, so a missing cover rendered as a near-white slab
   inside a dark card. Track the card surface instead. */
.img-placeholder {
  background: var(--surface-elevated);
}
.img-fallback {
  background: var(--surface-elevated);
  color: var(--text-muted);
}
</style>
